import { useEffect, useMemo, useState } from 'react';
import { Activity, Ban, Brain, Database, Eye, Loader2, RefreshCw, Search, Shield, Trash2 } from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';
import { NextBestQueryPanel } from './NextBestQueryPanel';

type Provider = 'sentinel' | 'mde';

type Verdict = 'No workspace hits' | 'Workspace hits found' | 'Review required' | string;

interface TiIndicator {
  provider: Provider;
  id: string;
  name?: string;
  value: string;
  type: string;
  title?: string;
  source?: string;
  confidence?: number | null;
  severity?: string | null;
  action?: string | null;
  status: string;
  validFrom?: string | null;
  validUntil?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  tags: string[];
  raw?: unknown;
}

interface NavigatorResponse {
  provider: Provider;
  customerId: string;
  count: number;
  items: TiIndicator[];
  nextLink?: string | null;
  raw?: unknown;
}

interface ReadResponse {
  provider: Provider;
  customerId: string;
  item: TiIndicator;
  raw: unknown;
}

interface TableHit {
  table: string;
  hitCount: number;
  firstSeen: string | null;
  lastSeen: string | null;
}

interface IocAnalysisResult {
  verdict: Verdict;
  summary: string;
  riskFactors: string[];
  recommendations: string[];
  tableHits: TableHit[];
  searchedTablesNote: string;
  kql: string;
  sampleRows: Record<string, unknown>[];
  aiMode: 'azure-openai' | 'deterministic';
  generatedAt: string;
}

interface AnalyzeResponse {
  ok: boolean;
  analysis: IocAnalysisResult;
  kqlError?: string | null;
}

const SENTINEL_TYPES = [
  { value: 'all', label: 'All Sentinel indicators' },
  { value: 'ipv4-addr', label: 'IPv4 address' },
  { value: 'ipv6-addr', label: 'IPv6 address' },
  { value: 'domain-name', label: 'Domain' },
  { value: 'url', label: 'URL' },
  { value: 'file-md5', label: 'File MD5' },
  { value: 'file-sha1', label: 'File SHA1' },
  { value: 'file-sha256', label: 'File SHA256' },
  { value: 'email-addr', label: 'Email address' },
];

const MDE_TYPES = [
  { value: 'all', label: 'All MDE indicators' },
  { value: 'IpAddress', label: 'IP address' },
  { value: 'DomainName', label: 'Domain' },
  { value: 'Url', label: 'URL' },
  { value: 'FileMd5', label: 'File MD5' },
  { value: 'FileSha1', label: 'File SHA1' },
  { value: 'FileSha256', label: 'File SHA256' },
];

const MDE_ACTIONS = ['all', 'Audit', 'Alert', 'AlertAndBlock', 'Block', 'BlockAndRemediate', 'Allowed'];
const MDE_SEVERITIES = ['all', 'Informational', 'Low', 'Medium', 'High'];

function toErrorMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function shortValue(value?: string, max = 72): string {
  if (!value) return '-';
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function statusClass(status: string): string {
  const normalized = status.toLowerCase();
  if (normalized.includes('active')) return 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-200';
  if (normalized.includes('revoked') || normalized.includes('expired')) return 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-200';
  return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200';
}

function verdictClass(verdict: Verdict): string {
  if (verdict === 'Workspace hits found') return 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-200';
  if (verdict === 'No workspace hits') return 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-200';
  return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-200';
}

function safeJson(value: unknown): string {
  try { return JSON.stringify(value ?? {}, null, 2); } catch { return String(value); }
}

export function ThreatIntelNavigator({ onRunKql }: { onRunKql?: (query: string) => void }) {
  const { activeCustomerId, activeCustomer } = useCustomers();
  const { canCapability } = useAuth();
  const canList = canCapability('threat_intel', 'list_ioc');
  const canRead = canCapability('threat_intel', 'read_ioc');
  const canDelete = canCapability('threat_intel', 'delete_ioc');
  const canRevoke = canCapability('threat_intel', 'revoke_ioc');
  const canAnalyze = canCapability('threat_intel', 'analyze_ioc');
  const canNextBestQuery = canCapability('next_best_query', 'generate');

  const [provider, setProvider] = useState<Provider>('sentinel');
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [limit, setLimit] = useState(50);
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [mdeAction, setMdeAction] = useState('all');
  const [mdeSeverity, setMdeSeverity] = useState('all');
  const [items, setItems] = useState<TiIndicator[]>([]);
  const [selected, setSelected] = useState<TiIndicator | null>(null);
  const [rawDetails, setRawDetails] = useState<unknown | null>(null);
  const [analysis, setAnalysis] = useState<IocAnalysisResult | null>(null);
  const [kqlError, setKqlError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const [lookbackDays, setLookbackDays] = useState(30);
  const [pivotAutoLoad, setPivotAutoLoad] = useState(false);

  const typeOptions = provider === 'sentinel' ? SENTINEL_TYPES : MDE_TYPES;

  useEffect(() => {
    setType('all');
    setSelected(null);
    setRawDetails(null);
    setAnalysis(null);
    setKqlError(null);
  }, [provider]);

  useEffect(() => {
    const raw = localStorage.getItem('ks-ti-navigator-focus');
    if (!raw) return;
    try {
      const pivot = JSON.parse(raw) as { value?: string };
      if (pivot.value) { setProvider('sentinel'); setQuery(String(pivot.value)); setType('all'); setLimit(50); setPivotAutoLoad(true); }
    } catch { /* ignore malformed local pivot */ }
    localStorage.removeItem('ks-ti-navigator-focus');
  }, []);

  useEffect(() => {
    if (!pivotAutoLoad || !canList) return;
    setPivotAutoLoad(false);
    void loadIndicators();
  }, [pivotAutoLoad, canList, provider, query]);

  async function loadIndicators() {
    if (!canList) return;
    setLoading(true);
    setError(null);
    setAnalysis(null);
    setKqlError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('customerId', activeCustomerId);
      qs.set('provider', provider);
      qs.set('limit', String(limit));
      if (query.trim()) qs.set('q', query.trim());
      if (type !== 'all') qs.set('type', type);
      if (provider === 'sentinel') {
        qs.set('includeDisabled', String(includeDisabled));
      } else {
        if (mdeAction !== 'all') qs.set('action', mdeAction);
        if (mdeSeverity !== 'all') qs.set('severity', mdeSeverity);
      }
      const data = await apiFetch<NavigatorResponse>(`/threat-intel/navigator/indicators?${qs.toString()}`);
      const nextItems = data.items ?? [];
      setItems(nextItems);
      setSelected((current) => {
        if (!current) return nextItems[0] ?? null;
        return nextItems.find((item) => item.id === current.id && item.provider === current.provider) ?? nextItems[0] ?? null;
      });
      setRawDetails(null);
      setLoadedAt(new Date().toISOString());
    } catch (e) {
      setError(toErrorMessage(e, 'Could not load indicators.'));
    } finally {
      setLoading(false);
    }
  }

  async function readIndicator(item = selected) {
    if (!item || !canRead) return;
    setBusyId(`read:${item.id}`);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('customerId', activeCustomerId);
      if (item.value) qs.set('value', item.value);
      const data = await apiFetch<ReadResponse>(`/threat-intel/navigator/indicators/${item.provider}/${encodeURIComponent(item.id)}?${qs.toString()}`);
      setSelected(data.item);
      setRawDetails(data.raw);
      setShowRaw(true);
    } catch (e) {
      setError(toErrorMessage(e, 'Could not read indicator.'));
    } finally {
      setBusyId(null);
    }
  }

  async function deleteIndicator(item = selected) {
    if (!item || !canDelete) return;
    const ok = window.confirm(`Delete this ${item.provider.toUpperCase()} IoC?\n\n${item.value || item.id}\n\nThis action cannot be undone from KloudSecure.`);
    if (!ok) return;
    setBusyId(`delete:${item.id}`);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('customerId', activeCustomerId);
      await apiFetch<{ ok: boolean }>(`/threat-intel/navigator/indicators/${item.provider}/${encodeURIComponent(item.id)}?${qs.toString()}`, { method: 'DELETE' });
      setItems((rows) => rows.filter((row) => !(row.provider === item.provider && row.id === item.id)));
      setSelected((current) => current?.id === item.id ? null : current);
      setRawDetails(null);
      setAnalysis(null);
    } catch (e) {
      setError(toErrorMessage(e, 'Could not delete indicator.'));
    } finally {
      setBusyId(null);
    }
  }

  async function revokeIndicator(item = selected) {
    if (!item || !canRevoke) return;
    const ok = window.confirm(`Revoke/expire this ${item.provider.toUpperCase()} IoC?\n\n${item.value || item.id}`);
    if (!ok) return;
    setBusyId(`revoke:${item.id}`);
    setError(null);
    try {
      const data = await apiFetch<{ item?: TiIndicator; raw?: unknown }>(`/threat-intel/navigator/indicators/${item.provider}/${encodeURIComponent(item.id)}/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: activeCustomerId, value: item.value }),
      });
      const updated = data.item ?? { ...item, status: 'revoked', validUntil: new Date().toISOString() };
      setItems((rows) => rows.map((row) => (row.provider === item.provider && row.id === item.id ? updated : row)));
      setSelected(updated);
      setRawDetails(data.raw ?? null);
    } catch (e) {
      setError(toErrorMessage(e, 'Could not revoke indicator.'));
    } finally {
      setBusyId(null);
    }
  }

  async function analyzeIndicator(item = selected) {
    if (!item || !canAnalyze) return;
    setAnalyzing(true);
    setError(null);
    setAnalysis(null);
    setKqlError(null);
    try {
      const data = await apiFetch<AnalyzeResponse>('/threat-intel/navigator/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: activeCustomerId, provider: item.provider, indicator: item, lookbackDays }),
      });
      setAnalysis(data.analysis);
      setKqlError(data.kqlError ?? null);
    } catch (e) {
      setError(toErrorMessage(e, 'AI analysis failed.'));
    } finally {
      setAnalyzing(false);
    }
  }

  const selectedRaw = useMemo(() => rawDetails ?? selected?.raw ?? null, [rawDetails, selected]);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border bg-card shadow overflow-hidden">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 border-b border-border bg-muted/30 px-4 py-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Threat Intelligence Navigator</div>
            <h3 className="mt-1 text-xl font-bold text-foreground flex items-center gap-2"><Shield size={20} className="text-primary" /> Sentinel + MDE IoC browser</h3>
            <p className="mt-1 text-sm text-muted-foreground">List, read, revoke, delete and AI-analyze indicators for {activeCustomer?.name ?? activeCustomerId}.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setProvider('sentinel')} className={`rounded-xl px-3 py-2 text-sm font-semibold border ${provider === 'sentinel' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'}`}>Microsoft Sentinel</button>
            <button type="button" onClick={() => setProvider('mde')} className={`rounded-xl px-3 py-2 text-sm font-semibold border ${provider === 'mde' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'}`}>MDE</button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <label className="space-y-1 xl:col-span-2">
              <span className="text-xs font-semibold text-muted-foreground">Search</span>
              <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
                <Search size={15} className="text-muted-foreground" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void loadIndicators(); }} placeholder="Value, title, tag or keyword" className="w-full bg-transparent text-sm outline-none" />
              </div>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-semibold text-muted-foreground">Type</span>
              <select value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
                {typeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-semibold text-muted-foreground">Limit</span>
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <div className="flex items-end">
              <button type="button" onClick={() => void loadIndicators()} disabled={!canList || loading} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow hover:opacity-90 disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Load IoCs
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {provider === 'sentinel' ? (
              <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" checked={includeDisabled} onChange={(e) => setIncludeDisabled(e.target.checked)} /> Include disabled/revoked indicators
              </label>
            ) : (
              <>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-muted-foreground">MDE action</span>
                  <select value={mdeAction} onChange={(e) => setMdeAction(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
                    {MDE_ACTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-muted-foreground">MDE severity</span>
                  <select value={mdeSeverity} onChange={(e) => setMdeSeverity(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
                    {MDE_SEVERITIES.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </label>
              </>
            )}
            <div className="text-xs text-muted-foreground md:text-right md:col-start-3 self-end">
              {loadedAt ? `Loaded ${items.length} item(s) at ${formatDate(loadedAt)}` : 'No navigator load has run yet.'}
            </div>
          </div>

          {!canList && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">Your role does not have Threat Intelligence → List IoCs permission.</div>}
          {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">{error}</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_430px] gap-5 items-start">
        <div className="rounded-2xl border border-border bg-card shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-3 text-left">Value</th>
                  <th className="px-3 py-3 text-left">Name / Title</th>
                  <th className="px-3 py-3 text-left">Type</th>
                  <th className="px-3 py-3 text-left">Source</th>
                  <th className="px-3 py-3 text-left">Confidence / Severity</th>
                  <th className="px-3 py-3 text-left">Action / Status</th>
                  <th className="px-3 py-3 text-left">Valid until</th>
                  <th className="px-3 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading && <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground"><Loader2 size={18} className="mx-auto mb-2 animate-spin" />Loading indicators…</td></tr>}
                {!loading && items.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">No indicators loaded. Choose Sentinel/MDE and click Load IoCs.</td></tr>}
                {!loading && items.map((item) => {
                  const active = selected?.id === item.id && selected?.provider === item.provider;
                  return (
                    <tr key={`${item.provider}:${item.id}:${item.value}`} className={`${active ? 'bg-primary/5' : 'hover:bg-muted/40'} cursor-pointer`} onClick={() => { setSelected(item); setRawDetails(null); setAnalysis(null); setKqlError(null); }}>
                      <td className="px-3 py-3 font-mono text-xs max-w-[260px]" title={item.value}>{shortValue(item.value, 80)}</td>
                      <td className="px-3 py-3 max-w-[280px]"><div className="font-medium text-foreground">{shortValue(item.title || item.name || item.id, 70)}</div><div className="mt-0.5 text-[11px] text-muted-foreground">{shortValue(item.id, 70)}</div></td>
                      <td className="px-3 py-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">{item.type || 'indicator'}</span></td>
                      <td className="px-3 py-3 text-muted-foreground">{shortValue(item.source, 48)}</td>
                      <td className="px-3 py-3">{item.confidence ?? item.severity ?? '-'}</td>
                      <td className="px-3 py-3"><div className="space-y-1">{item.action && <div className="text-xs font-semibold">{item.action}</div>}<span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusClass(item.status)}`}>{item.status || 'unknown'}</span></div></td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{formatDate(item.validUntil)}</td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-wrap gap-1">
                          <button type="button" disabled={!canRead || busyId === `read:${item.id}`} onClick={() => void readIndicator(item)} className="rounded-lg border border-border bg-background p-2 hover:bg-muted disabled:opacity-50" title="Read details"><Eye size={14} /></button>
                          <button type="button" disabled={!canAnalyze || analyzing} onClick={() => void analyzeIndicator(item)} className="rounded-lg border border-border bg-background p-2 hover:bg-muted disabled:opacity-50" title="AI Analysis"><Brain size={14} /></button>
                          <button type="button" disabled={!canRevoke || busyId === `revoke:${item.id}`} onClick={() => void revokeIndicator(item)} className="rounded-lg border border-border bg-background p-2 hover:bg-muted disabled:opacity-50" title="Revoke/expire"><Ban size={14} /></button>
                          <button type="button" disabled={!canDelete || busyId === `delete:${item.id}`} onClick={() => void deleteIndicator(item)} className="rounded-lg border border-border bg-background p-2 text-rose-600 hover:bg-rose-50 disabled:opacity-50" title="Delete"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="space-y-5 2xl:sticky 2xl:top-4">
          <div className="rounded-2xl border border-border bg-card p-5 shadow">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Selected IoC</div>
                <h4 className="mt-1 font-bold text-foreground break-all">{selected ? shortValue(selected.value || selected.id, 120) : 'None selected'}</h4>
              </div>
              {selected && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusClass(selected.status)}`}>{selected.status}</span>}
            </div>

            {selected ? (
              <div className="mt-4 space-y-3 text-sm">
                <Detail label="Provider" value={selected.provider.toUpperCase()} />
                <Detail label="Type" value={selected.type} />
                <Detail label="Title" value={selected.title || selected.name || '-'} />
                <Detail label="Source" value={selected.source || '-'} />
                <Detail label="Valid from" value={formatDate(selected.validFrom)} />
                <Detail label="Valid until" value={formatDate(selected.validUntil)} />
                {selected.tags?.length > 0 && <div><div className="text-xs font-semibold text-muted-foreground">Tags</div><div className="mt-1 flex flex-wrap gap-1">{selected.tags.slice(0, 12).map((tag) => <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-[11px]">{tag}</span>)}</div></div>}

                <div className="grid grid-cols-2 gap-2 pt-2">
                  <button type="button" disabled={!canRead || busyId === `read:${selected.id}`} onClick={() => void readIndicator()} className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-muted disabled:opacity-60"><Eye size={14} /> Read</button>
                  <button type="button" disabled={!canAnalyze || analyzing} onClick={() => void analyzeIndicator()} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60">{analyzing ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />} AI Analysis</button>
                  <button type="button" disabled={!canRevoke || busyId === `revoke:${selected.id}`} onClick={() => void revokeIndicator()} className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-muted disabled:opacity-60"><Ban size={14} /> Revoke</button>
                  <button type="button" disabled={!canDelete || busyId === `delete:${selected.id}`} onClick={() => void deleteIndicator()} className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-background px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-60"><Trash2 size={14} /> Delete</button>
                </div>
              </div>
            ) : <p className="mt-4 text-sm text-muted-foreground">Select an IoC from the table to view details and actions.</p>}
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow">
            <div className="flex items-center justify-between gap-3">
              <h4 className="font-bold text-foreground flex items-center gap-2"><Brain size={18} className="text-primary" /> AI Analysis</h4>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">Lookback
                <select value={lookbackDays} onChange={(e) => setLookbackDays(Number(e.target.value))} className="rounded-lg border border-border bg-background px-2 py-1 text-xs">
                  {[7, 14, 30, 60, 90].map((n) => <option key={n} value={n}>{n}d</option>)}
                </select>
              </label>
            </div>
            {analyzing && <div className="mt-4 rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground"><Loader2 size={16} className="mr-2 inline animate-spin" />Searching the scoped workspace and preparing analysis…</div>}
            {!analyzing && !analysis && <p className="mt-3 text-sm text-muted-foreground">Click AI Analysis to search the selected customer workspace across available tables and summarize where the IoC appears.</p>}
            {analysis && (
              <div className="mt-4 space-y-4 text-sm">
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${verdictClass(analysis.verdict)}`}>{analysis.verdict}</span>
                <p className="text-muted-foreground">{analysis.summary}</p>
                {kqlError && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">Workspace search warning: {kqlError}</div>}
                <MiniList title="Risk factors" items={analysis.riskFactors} />
                <MiniList title="Recommendations" items={analysis.recommendations} />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Table hits</div>
                  <div className="mt-2 space-y-2">
                    {analysis.tableHits.length === 0 && <div className="rounded-xl bg-muted/30 p-3 text-xs text-muted-foreground">No table hits returned.</div>}
                    {analysis.tableHits.slice(0, 10).map((hit) => (
                      <div key={hit.table} className="rounded-xl border border-border bg-background p-3">
                        <div className="flex items-center justify-between gap-2"><span className="font-semibold">{hit.table}</span><span className="rounded-full bg-muted px-2 py-0.5 text-[11px]">{hit.hitCount} hit(s)</span></div>
                        <div className="mt-1 text-[11px] text-muted-foreground">First: {formatDate(hit.firstSeen)} · Last: {formatDate(hit.lastSeen)}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <details className="rounded-xl border border-border bg-background p-3">
                  <summary className="cursor-pointer text-xs font-semibold text-muted-foreground">Show generated KQL</summary>
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px]">{analysis.kql}</pre>
                </details>
              </div>
            )}
          </div>

          {selected && canNextBestQuery && (
            <NextBestQueryPanel
              customerId={activeCustomerId}
              context={{
                kind: 'ioc',
                customerId: activeCustomerId,
                value: selected.value,
                type: selected.type,
                title: selected.title || selected.name || selected.id,
                provider: selected.provider,
                lookback: `${lookbackDays}d`,
                extra: selected,
              }}
              onRunKql={onRunKql}
              compact
            />
          )}

          {selected && (
            <div className="rounded-2xl border border-border bg-card p-5 shadow">
              <button type="button" onClick={() => setShowRaw((v) => !v)} className="flex w-full items-center justify-between gap-2 text-left font-bold text-foreground">
                <span className="flex items-center gap-2"><Database size={18} className="text-primary" /> Raw details</span>
                <span className="text-xs text-muted-foreground">{showRaw ? 'Hide' : 'Show'}</span>
              </button>
              {showRaw && <pre className="mt-3 max-h-96 overflow-auto rounded-xl bg-muted/40 p-3 text-[11px] whitespace-pre-wrap">{safeJson(selectedRaw)}</pre>}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div><div className="text-xs font-semibold text-muted-foreground">{label}</div><div className="mt-0.5 break-words text-foreground">{value}</div></div>;
}

function MiniList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      <ul className="mt-2 space-y-1 text-muted-foreground">
        {(items.length ? items : ['-']).map((item, idx) => <li key={`${title}-${idx}`} className="flex gap-2"><Activity size={13} className="mt-0.5 shrink-0 text-primary" /><span>{item}</span></li>)}
      </ul>
    </div>
  );
}
